// A paused project's QA stops where it stood. The container is gone, but the rows it left behind keep
// whatever non-terminal status they held at that moment — so a test run reads "In Progress", and the
// journeys and journey versions it was exercising report a last run that has been "In Progress" ever
// since. Nothing is executing behind any of them, and nothing will until the project resumes.
//
// The chat's run cards already substitute "Paused" for that stale live label. This module is that same
// substitution for every other surface (run detail, run table, journeys, journey versions, journey
// runs), plus the part the badge alone cannot say: WHY the project is paused, and what resumes it.

/** The pause a project is currently under, or null when its QA is not paused. */
export interface ProjectPause {
  /** `projects.paused_reason` — null for a plain manual pause, which has no billing fix attached. */
  reason: string | null
}

/** Reads a fetched project's pause, or null when it is not paused. */
export function projectPause(
  project?: { status?: string | null; paused_reason?: string | null } | null,
): ProjectPause | null {
  if (!project || project.status !== 'paused') return null
  return { reason: project.paused_reason ?? null }
}

// The statuses that claim work is happening RIGHT NOW, and so cannot be true under a pause.
//
// 'queued' is deliberately absent: a queued run really is still waiting its turn, and its position in
// the queue is the more useful read. 'awaiting-deployment' is absent for the reason the run table
// already gives — a revision with no preview deployment is blocked in FRONT of the pause, and saying
// "paused" would hide the thing that actually has to be fixed. 'awaiting-access' is included: the
// access-gate wait needs a live container, which a pause has taken away.
const FROZEN_BY_PAUSE = new Set(['in-progress', 'pending', 'awaiting-access'])

/**
 * The status to display: 'paused' in place of a stale claim that work is in flight, otherwise the
 * stored status unchanged. Safe to call with no pause (an active project passes everything through).
 */
export function pausedStatus<T extends string | null | undefined>(status: T, pause: ProjectPause | null): T | 'paused' {
  return pause && typeof status === 'string' && FROZEN_BY_PAUSE.has(status) ? 'paused' : status
}

/**
 * Hover text for a badge showing a project pause. "Paused" on its own answers "why isn't this running?"
 * with "because it's paused", which only moves the question — so name what ended the work and what
 * brings it back. Returns undefined when there is no pause, so callers can fall back to their own copy.
 */
export function pauseTooltip(pause: ProjectPause | null): string | undefined {
  if (!pause) return undefined
  switch (pause.reason) {
    case 'no_credits':
      return 'Paused: the account ran out of credits, so this stopped where it was. Fix billing to resume it.'
    case 'budget_exhausted':
      return 'Paused: this project reached its QA credit budget, so this stopped where it was. Raise the budget to resume it.'
    case 'awaiting_start':
      return 'Paused: QA has not been started for this project yet, so nothing is running.'
    default:
      return 'Paused: the project is paused, so this stopped where it was. Resume the project to continue it.'
  }
}
